/**
 * AdminClientGrid - AG Grid implementation for Client Management
 *
 * Displays clients in a sortable, filterable grid with:
 * - Client ID, Name, Folder, Status, File Count
 * - Action buttons (Edit, Delete)
 */

import React, { useMemo } from "react";
import styled from "styled-components";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  AllCommunityModule,
  themeQuartz,
} from "ag-grid-community";
import type {
  ColDef,
  ICellRendererParams,
  ValueGetterParams,
} from "ag-grid-community";
import { IconButton } from "@flexport/latitude-typescript";

// Register AG Grid v34 modules (required in v34+)
ModuleRegistry.registerModules([AllCommunityModule]);

// Configure AG Grid v34 theme using new Theming API
const gridTheme = themeQuartz;

/**
 * Grid wrapper with fixed height
 */
const GridWrapper = styled.div`
  width: 100%;
  height: 600px;
  margin-top: 20px;
`;

/**
 * Tooltip wrapper that properly shows tooltip when hovering over button and its children
 * The title attribute on the wrapper will trigger tooltips for the entire area
 */
const TooltipWrapper = styled.div`
  display: inline-flex;
  position: relative;
  cursor: pointer;

  /* Ensure button inherits the cursor and doesn't block tooltip */
  button {
    cursor: pointer;
  }
`;

/**
 * Actions container for button spacing with centered alignment
 */
const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

/**
 * Status badge for upload completion
 */
const StatusBadge = styled.span<{ complete: boolean }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background-color: ${(props) => (props.complete ? "#28a745" : "#6c757d")};
`;

/**
 * Client row data interface
 */
export interface ClientRow {
  clientId: string;
  client_name: string;
  folder: string;
  upload_complete: boolean;
  files?: any[];
}

/**
 * Props for AdminClientGrid component
 */
interface AdminClientGridProps {
  /** Array of client rows to display */
  rows: ReadonlyArray<ClientRow>;
  /** Callback when edit button is clicked */
  onEdit: (client: ClientRow) => void;
  /** Callback when delete button is clicked */
  onDelete: (clientId: string) => void;
}

/**
 * Status cell renderer - displays Complete/In Progress badge
 */
const StatusCellRenderer: React.FC<ICellRendererParams<ClientRow>> = (
  params,
) => {
  const { data } = params;
  if (!data) return null;

  return (
    <StatusBadge complete={data.upload_complete}>
      {data.upload_complete ? "Complete" : "In Progress"}
    </StatusBadge>
  );
};

/**
 * Actions cell renderer - displays Edit and Delete icon buttons with tooltips
 */
const ActionsCellRenderer: React.FC<
  ICellRendererParams<ClientRow> & {
    onEdit: (client: ClientRow) => void;
    onDelete: (clientId: string) => void;
  }
> = (params) => {
  const { data } = params;
  const onEdit = (params as any).onEdit;
  const onDelete = (params as any).onDelete;

  if (!data) return null;

  return (
    <ActionsContainer>
      <TooltipWrapper title="Edit client details">
        <IconButton
          iconName="pencil"
          label="Edit"
          labelAriaOnly={true}
          iconAlignment="left"
          intent="basic"
          kind="hollow"
          size="s"
          type="button"
          isLoading={false}
          width="responsive"
          height={{ type: "fixed" }}
          disabled={false}
          temporaryUnsafeSetSolidKindHack={false}
          onClick={() => onEdit(data)}
          testId={`edit-client-${data.clientId}`}
        />
      </TooltipWrapper>
      <TooltipWrapper title="Delete client">
        <IconButton
          iconName="trash"
          label="Delete"
          labelAriaOnly={true}
          iconAlignment="left"
          intent="danger"
          kind="hollow"
          size="s"
          type="button"
          isLoading={false}
          width="responsive"
          height={{ type: "fixed" }}
          disabled={false}
          temporaryUnsafeSetSolidKindHack={false}
          onClick={() => onDelete(data.clientId)}
          testId={`delete-client-${data.clientId}`}
        />
      </TooltipWrapper>
    </ActionsContainer>
  );
};

/**
 * Files count value getter
 */
const filesCountGetter = (params: ValueGetterParams<ClientRow>): number => {
  return params.data?.files?.length || 0;
};

/**
 * AdminClientGrid Component
 *
 * Displays clients in an AG Grid table with sorting, filtering,
 * and action buttons.
 */
const AdminClientGrid: React.FC<AdminClientGridProps> = ({
  rows,
  onEdit,
  onDelete,
}) => {
  /**
   * Column definitions for AG Grid
   */
  const columnDefs = useMemo<ColDef<ClientRow>[]>(
    () => [
      {
        headerName: "ID",
        field: "clientId",
        width: 100,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Client Name",
        field: "client_name",
        flex: 1,
        minWidth: 200,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Folder",
        field: "folder",
        flex: 1,
        minWidth: 150,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Status",
        field: "upload_complete",
        cellRenderer: StatusCellRenderer,
        width: 130,
        sortable: true,
        filter: false,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Files",
        valueGetter: filesCountGetter,
        width: 100,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Actions",
        sortable: false,
        filter: false,
        width: 120,
        cellRenderer: ActionsCellRenderer,
        cellRendererParams: { onEdit, onDelete },
        suppressHeaderMenuButton: true,
      },
    ],
    [onEdit, onDelete],
  );

  /**
   * Default column configuration
   */
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressHeaderMenuButton: true,
    }),
    [],
  );

  return (
    <GridWrapper data-testid="client-grid">
      <AgGridReact<ClientRow>
        theme={gridTheme}
        rowData={rows as ClientRow[]}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowId={(params) => params.data.clientId}
        suppressCellFocus={false}
      />
    </GridWrapper>
  );
};

export default AdminClientGrid;
